/**
 * useProjects.ts
 *
 * Backs ProjectPicker's search box: a 250ms-debounced query value driving
 * a TanStack Query GET /projects/search. The debounce timer is a local
 * (non-fetching) effect; the actual network call stays entirely inside
 * TanStack Query.
 */
import { useEffect, useState } from "react";
import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "../api/client";
import type {
  ApiError,
  IndexStatus,
  ProjectSearchResponse,
  SearchFilters,
} from "../api/types";

const DEBOUNCE_MS = 250;
/** The index only changes on the nightly crawl or a manual import. */
const INDEX_STATUS_STALE_MS = 5 * 60 * 1000;

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

export function useProjectSearch(
  query: string,
  filters?: SearchFilters,
): UseQueryResult<ProjectSearchResponse, ApiError> {
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);

  return useQuery<ProjectSearchResponse, ApiError>({
    // Filters are part of the key: changing scope must refetch, not serve the
    // previous scope's cached results.
    queryKey: ["projects", "search", debouncedQuery, filters ?? null],
    queryFn: () => api.searchProjects(debouncedQuery, filters),
    placeholderData: keepPreviousData,
  });
}

/**
 * Backs the picker's "this is still demo data" banner. Failures are swallowed
 * into an absent banner rather than an error state — not knowing the index's
 * provenance must never block filing.
 */
export function useIndexStatus(): UseQueryResult<IndexStatus, ApiError> {
  return useQuery<IndexStatus, ApiError>({
    queryKey: ["index", "status"],
    queryFn: () => api.getIndexStatus(),
    staleTime: INDEX_STATUS_STALE_MS,
    retry: false,
  });
}
